/**
 * SPEC.md §6 — image/embed export. M5.2 (issue #22) lands the SVG path:
 * every export starts by parsing the render loop's current SVG markup
 * string fresh via `DOMParser`, never the live, on-screen SVG node — every
 * call produces its own detached document, so "clone the node, never
 * mutate the live SVG" holds trivially and this is fully testable in jsdom
 * (unlike `mermaid.render()` itself; see mermaidRender.ts).
 *
 * M5.3 (issue #23) adds the raster path on top: the same fixed-up SVG
 * string, `encodeURIComponent`-encoded into a `data:image/svg+xml` URL
 * (never `btoa` — gotcha 5, breaks on non-ASCII labels) → `Image` →
 * `<canvas>` → `toBlob`. `clampScale` is pure math and unit-tested; the
 * `Image`/canvas pipeline itself needs a real browser (same limitation as
 * `mermaid.render()`), so it's manually verified and covered by e2e instead.
 *
 * M5.4 (issue #24) adds the "Copy as" code generators: six pure functions
 * of `{ source, svg }`, no rendering, no DOM beyond what `fixSvgMarkup`
 * already does — every one is unit-testable.
 *
 * M5.5 (issue #25) adds a seventh: mermaid.live's own link format, the one
 * Copy-as target needing a new dependency (`pako`, for the deflate
 * compression their URL scheme uses) — approved and added for this single
 * purpose (CLAUDE.md rule 3).
 */
import { deflate } from 'pako';
import { MERMAID_VERSION } from './mermaidVersion';

const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const XMLNS_NS = 'http://www.w3.org/2000/xmlns/';

export interface ExportSvgOptions {
  /** Off (default) bakes in a white background rect; on omits it. */
  transparent: boolean;
}

/** Exported for svgDimensions.ts's reuse — the one other place that needs viewBox's numbers, not just export.ts's own fixSvgMarkup. */
export function parseViewBox(value: string): { minX: number; minY: number; width: number; height: number } | null {
  const parts = value.trim().split(/\s+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  const [minX, minY, width, height] = parts as [number, number, number, number];
  return { minX, minY, width, height };
}

/**
 * Gotchas 1-4, 6, 7 from SPEC.md §6: explicit xmlns/xmlns:xlink, width/height
 * taken from viewBox and set as explicit attributes (never
 * getBoundingClientRect — that reflects the live pan/zoom transform, not the
 * diagram's own size), the inline max-width style Mermaid adds removed, and
 * a white background baked in behind the content unless `transparent` is
 * set. Pan/zoom on screen never touches this function's input at all, so it
 * has zero effect on the output by construction.
 */
export function fixSvgMarkup(svgMarkup: string, options: ExportSvgOptions): string {
  const doc = new DOMParser().parseFromString(svgMarkup, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Could not parse SVG markup');
  }
  const svg = doc.documentElement;

  svg.setAttributeNS(XMLNS_NS, 'xmlns', SVG_NS);
  svg.setAttributeNS(XMLNS_NS, 'xmlns:xlink', XLINK_NS);

  const viewBoxAttr = svg.getAttribute('viewBox');
  const viewBox = viewBoxAttr === null ? null : parseViewBox(viewBoxAttr);
  if (viewBox === null) {
    // mermaid.render() always emits a viewBox (confirmed against the pinned
    // version) — this is a "should never happen" assertion, not a case
    // export.ts's callers are expected to handle.
    throw new Error('Could not derive export dimensions: SVG has no valid viewBox');
  }
  svg.setAttribute('width', String(viewBox.width));
  svg.setAttribute('height', String(viewBox.height));

  const styleAttr = svg.getAttribute('style');
  if (styleAttr !== null) {
    const cleaned = styleAttr
      .split(';')
      .map((declaration) => declaration.trim())
      .filter((declaration) => declaration.length > 0 && !/^max-width\s*:/i.test(declaration))
      .join('; ');
    if (cleaned.length > 0) svg.setAttribute('style', cleaned);
    else svg.removeAttribute('style');
  }

  if (!options.transparent) {
    const background = doc.createElementNS(SVG_NS, 'rect');
    background.setAttribute('x', String(viewBox.minX));
    background.setAttribute('y', String(viewBox.minY));
    background.setAttribute('width', String(viewBox.width));
    background.setAttribute('height', String(viewBox.height));
    // Independent of --sheet in tokens.css (SPEC.md §9) on purpose: that
    // token is a UI theme value the export path has no reason to depend on,
    // and this white is written into the exported file's own content, not
    // this app's chrome (CLAUDE.md rule 10 governs the latter, not the former).
    background.setAttribute('fill', '#ffffff');
    svg.insertBefore(background, svg.firstChild);
  }

  return new XMLSerializer().serializeToString(svg);
}

export function svgMarkupToBlob(svgMarkup: string): Blob {
  return new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
}

/**
 * Standard blob-download-via-hidden-anchor pattern. Browser-only — can't be
 * meaningfully verified in jsdom (no real download to observe), so this is
 * covered by manual verification and e2e's `page.waitForEvent('download')`
 * instead of a unit test.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * The one save-SVG flow, shared by the Export menu's "Save SVG" item and
 * App.tsx's `Cmd/Ctrl+Shift+S` shortcut — fix, blob, download, one call.
 */
export function saveSvg(svg: string, filename: string, transparent: boolean): void {
  const fixed = fixSvgMarkup(svg, { transparent });
  downloadBlob(svgMarkupToBlob(fixed), filename);
}

export const SCALE_OPTIONS = [1, 2, 4] as const;
export type Scale = (typeof SCALE_OPTIONS)[number];

export type RasterFormat = 'png' | 'jpeg' | 'webp';

interface RasterFormatMeta {
  label: string;
  mimeType: string;
  /** Download filename extension — deliberately `jpg`, the conventional default; SPEC.md §6 doesn't specify one. */
  extension: string;
}

/**
 * The one place `RasterFormat` gets its label/MIME/extension — a new format
 * only ever needs one entry here, not edits scattered across this file and
 * ExportMenu.tsx. `RASTER_FORMATS` (the iteration order the menu renders
 * in) is derived from it, so the two can't drift apart.
 */
export const RASTER_FORMAT_META: Record<RasterFormat, RasterFormatMeta> = {
  png: { label: 'PNG', mimeType: 'image/png', extension: 'png' },
  jpeg: { label: 'JPEG', mimeType: 'image/jpeg', extension: 'jpg' },
  webp: { label: 'WebP', mimeType: 'image/webp', extension: 'webp' },
};
export const RASTER_FORMATS = Object.keys(RASTER_FORMAT_META) as RasterFormat[];

const JPEG_QUALITY = 0.92;

/**
 * SPEC.md §6 gotcha 8: at 4× a wide diagram can exceed the browser's max
 * canvas dimensions (Safari is strictest), and `toBlob` returns a blank or
 * null result rather than throwing. 16384px is a conservative, widely-safe
 * per-side ceiling — comfortably under every major browser's own limit.
 * Tries the requested scale first, then each smaller option in
 * `SCALE_OPTIONS`, returning the largest that fits both dimensions. If even
 * 1x doesn't fit (a pathological diagram), returns 1x's dimensions
 * unclamped — there's no smaller option to fall back to, so this is the
 * browser's own failure mode to handle (per the gotcha above), not
 * something to design around here.
 */
export const MAX_CANVAS_DIMENSION = 16384;

export interface ClampedScale {
  scale: Scale;
  width: number;
  height: number;
}

export function clampScale(viewBoxWidth: number, viewBoxHeight: number, requestedScale: Scale): ClampedScale {
  const candidates = [...SCALE_OPTIONS].filter((s) => s <= requestedScale).sort((a, b) => b - a);
  for (const scale of candidates) {
    const width = Math.round(viewBoxWidth * scale);
    const height = Math.round(viewBoxHeight * scale);
    if (width <= MAX_CANVAS_DIMENSION && height <= MAX_CANVAS_DIMENSION) {
      return { scale, width, height };
    }
  }
  return { scale: 1, width: Math.round(viewBoxWidth), height: Math.round(viewBoxHeight) };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load the SVG for rasterization'));
    image.src = src;
  });
}

export interface RasterizeOptions {
  format: RasterFormat;
  scale: Scale;
  /** Ignored for JPEG, which has no alpha channel and always gets a white background. */
  transparent: boolean;
}

export interface RasterizeResult {
  blob: Blob;
  /** The scale actually used, after clamping — may be smaller than requested. */
  scale: Scale;
}

/**
 * The fixed-up SVG → `Image` → `<canvas>` → `toBlob` pipeline gotchas 2, 3,
 * 5, 8 describe. Not unit-testable (`Image`/canvas need a real browser) —
 * see this file's top doc comment.
 */
export async function rasterizeSvg(svg: string, options: RasterizeOptions): Promise<RasterizeResult> {
  const transparent = options.format === 'jpeg' ? false : options.transparent;
  const fixed = fixSvgMarkup(svg, { transparent });

  const fixedDoc = new DOMParser().parseFromString(fixed, 'image/svg+xml').documentElement;
  const naturalWidth = Number(fixedDoc.getAttribute('width'));
  const naturalHeight = Number(fixedDoc.getAttribute('height'));
  const clamped = clampScale(naturalWidth, naturalHeight, options.scale);

  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(fixed)}`;
  const image = await loadImage(dataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = clamped.width;
  canvas.height = clamped.height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Could not get a 2D canvas context');

  if (options.format === 'jpeg') {
    // Same independent-of-tokens.css reasoning as fixSvgMarkup's background
    // rect (CLAUDE.md rule 10 governs this app's own chrome, not a raster
    // file's baked-in pixels) — JPEG has no alpha channel, so this always
    // runs regardless of the transparent option above.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, clamped.width, clamped.height);
  }
  ctx.drawImage(image, 0, 0, clamped.width, clamped.height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(
      resolve,
      RASTER_FORMAT_META[options.format].mimeType,
      options.format === 'jpeg' ? JPEG_QUALITY : undefined,
    );
  });
  // Unlike fixSvgMarkup's viewBox throw (an assertion — "should never
  // happen"), a null blob here is SPEC.md §6 gotcha 8's own documented
  // failure mode ("Safari is strictest... returns a blank or null result
  // rather than throwing"), expected to surface occasionally at real-world
  // canvas-size extremes even after clamping. Both call sites below
  // (ExportMenu.tsx, App.tsx's Cmd/Ctrl+Shift+C handler) catch this by
  // design, not by coincidence — a failed rasterization just shows no
  // confirmation rather than a new error-surface built for this one path.
  if (blob === null) throw new Error(`Could not rasterize the diagram as ${options.format}`);

  return { blob, scale: clamped.scale };
}

/**
 * The one save-raster flow, mirroring `saveSvg` — rasterize, download,
 * return the scale actually used so the caller can name it in its
 * confirmation ("Saved PNG at 2×." even when 4× was requested).
 */
export async function saveRaster(svg: string, filename: string, options: RasterizeOptions): Promise<Scale> {
  const { blob, scale } = await rasterizeSvg(svg, options);
  downloadBlob(blob, filename);
  return scale;
}

/**
 * SPEC.md §6: "The single most-used action." Always PNG at 1x — a global
 * shortcut has no menu open to read a transparency toggle from, so it
 * stays predictable rather than silently depending on hidden state (same
 * reasoning as `Cmd/Ctrl+Shift+S`'s fixed default in App.tsx).
 */
export async function copyPngToClipboard(svg: string): Promise<void> {
  const { blob } = await rasterizeSvg(svg, { format: 'png', scale: 1, transparent: false });
  await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
}

/** What every "Copy as" generator needs: the current source, and the render loop's current SVG markup. */
export interface CopyAsContext {
  source: string;
  svg: string;
}

export function toMarkdownFence({ source }: CopyAsContext): string {
  return '```mermaid\n' + source + '\n```';
}

/**
 * Pinned to the app's own installed Mermaid version (mermaidVersion.ts) —
 * the pasted snippet renders identically to what the user saw in Plotter,
 * with no drift toward whatever `@latest` on the CDN happens to resolve to
 * later (CLAUDE.md rule 6's own reasoning for pinning, applied here too).
 */
export function toHtmlEmbed({ source }: CopyAsContext): string {
  return [
    `<pre class="mermaid">`,
    source,
    `</pre>`,
    `<script type="module">`,
    `  import mermaid from 'https://cdn.jsdelivr.net/npm/mermaid@${MERMAID_VERSION}/dist/mermaid.esm.min.mjs';`,
    `  mermaid.initialize({ startOnLoad: true });`,
    `</script>`,
  ].join('\n');
}

/** The raw fixed-up SVG markup, opaque by default — for pasting directly into HTML. */
export function toInlineSvg({ svg }: CopyAsContext): string {
  return fixSvgMarkup(svg, { transparent: false });
}

// Plain `btoa` only accepts Latin1 text — it throws on non-ASCII input
// (SPEC.md §6 gotcha 5's own warning, about a different data URI in this
// file, but the same underlying problem) and can't take raw binary bytes
// (like deflate output) at all. Converting bytes to a Latin1-safe binary
// string first is the standard, dependency-free way around both.
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function utf8ToBase64(text: string): string {
  return bytesToBase64(new TextEncoder().encode(text));
}

/** A base64 `data:image/svg+xml` URI for `<img src>`, from the fixed-up SVG. */
export function toDataUri({ svg }: CopyAsContext): string {
  const fixed = fixSvgMarkup(svg, { transparent: false });
  return `data:image/svg+xml;base64,${utf8ToBase64(fixed)}`;
}

// A Mermaid label can legitimately contain a backtick or `${` (e.g.
// `A["Uses \`git\`"]`); left unescaped, either would prematurely terminate
// or reinterpolate the generated snippet's own template literal.
function escapeForTemplateLiteral(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${');
}

export function toReactComponent({ source }: CopyAsContext): string {
  return [
    `import { useEffect, useRef } from 'react';`,
    `import mermaid from 'mermaid';`,
    ``,
    `const diagram = \``,
    escapeForTemplateLiteral(source),
    `\`;`,
    ``,
    `export default function Diagram() {`,
    `  const ref = useRef<HTMLDivElement>(null);`,
    ``,
    `  useEffect(() => {`,
    `    mermaid.initialize({ startOnLoad: false });`,
    `    if (!ref.current) return;`,
    `    mermaid.render('diagram', diagram).then(({ svg }) => {`,
    `      if (ref.current) ref.current.innerHTML = svg;`,
    `    });`,
    `  }, []);`,
    ``,
    `  return <div ref={ref} />;`,
    `}`,
  ].join('\n');
}

/**
 * Docusaurus's Mermaid support is site-wide config (`@docusaurus/theme-mermaid`),
 * not something a per-page fence can turn on — so this is a note above the
 * fence, not fabricated frontmatter that would look like it configures
 * something it doesn't.
 */
export function toMdx({ source }: CopyAsContext): string {
  return [
    `{/* This site needs @docusaurus/theme-mermaid enabled (docusaurus.config.js:`,
    `   markdown.mermaid: true, themes: ['@docusaurus/theme-mermaid']) for this fence to render. */}`,
    ``,
    '```mermaid',
    source,
    '```',
  ].join('\n');
}

/**
 * mermaid.live's own `#pako:<...>` edit-URL format — undocumented, so this
 * is reverse engineered by typing a known diagram into the real editor and
 * inspecting the URL it produced, then confirmed by loading a link built
 * this way back on the real site (SPEC.md §6: "a compatibility nicety, do
 * it last" — the one Copy-as target with an external dependency, isolated
 * in its own ticket for exactly that reason). The payload shape
 * (`{ code, mermaid, autoSync, updateDiagram }`) is a minimal subset of
 * what the real editor emits — extra fields it also sends (`pan`, `zoom`,
 * `renderCount`, `grid`) are UI-state only and not needed for the diagram
 * itself to load and render.
 */
export function toMermaidLiveLink({ source }: CopyAsContext): string {
  const payload = {
    code: source,
    mermaid: JSON.stringify({ theme: 'default' }),
    autoSync: true,
    updateDiagram: true,
  };
  // level: 9 is deflate's own max-compression setting — any valid deflate
  // level decodes identically on mermaid.live's side, this only affects the
  // link's length (favored here since it's meant to be pasted/shared).
  const deflated = deflate(JSON.stringify(payload), { level: 9 });
  const base64 = bytesToBase64(deflated);
  const base64url = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `https://mermaid.live/edit#pako:${base64url}`;
}
